import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import * as XLSX from "xlsx-js-style";
import { Menu } from "primereact/menu";
import { getOneMonthAgo } from "../CashInflow/CashInflow";
import SharedButton from "../../components/Shared/SharedButton";
import IncomeCard from "./IncomeCard";
import DatePicker from "../../components/Shared/DatePicker";
import useFetchData from "../../hooks/useFetchData";
import { FaFileExport } from "react-icons/fa6";
import { useSelector } from "react-redux";

export default function Income() {
  const [endDate, setEndDate] = useState(
    new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0],
  );
  const { user } = useSelector((state) => state.auth);
  const [startDate, setStartDate] = useState(getOneMonthAgo(endDate));
  const {
    data: incomeStatement,
    loading: isLoading,
    executeFetch,
  } = useFetchData();
  const balanceSheetRef = useRef(null);
  const iframeRef = useRef(null);
  const menuRef = useRef(null);
  const { t } = useTranslation();
  const lang = i18next.language;

  // loading, cashFlow, bankFlow
  const handleSubmit = () => {
    if (startDate && endDate) {
      executeFetch(
        "POST",
        `reports/income_statement?start_date=${startDate}&end_date=${endDate}`,
      );
    }
  };

  const handleReset = () => {
    const defaultEndDate = new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];
    const defaultStartDate = getOneMonthAgo(defaultEndDate);
    setEndDate(defaultEndDate);
    setStartDate(defaultStartDate);
    executeFetch(
      "POST",
      `reports/income_statement?start_date=${defaultStartDate}&end_date=${defaultEndDate}`,
    );
  };

  // useEffect(() => {
  //   executeFetch("POST", `reports/income_statement?start_date=${startDate}&end_date=${endDate}`);
  // }, [executeFetch]);

  const handlePrint = () => {
    const direction = lang === "ar" ? "rtl" : "ltr";
    const iframe = iframeRef.current;
    const doc = iframe.contentWindow.document;

    const businessNameEn = user?.Business?.name_en || "";
    const businessNameAr = user?.Business?.name_ar || "";
    const businessAddress = user?.Business?.address || "";
    const userEmail = user?.email || "";
    const userMobile = user?.mobile_number || "";
    const businessLogo = user?.Business?.logo || user?.Business?.logo_url;

    const expenses = incomeStatement?.data?.expenses?.total ?? 0;
    const revenue = incomeStatement?.data?.revenues?.total ?? 0;
    const netIncome = incomeStatement?.data?.net_income ?? 0;

    const tableHTML = `
      <div style="border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; font-family: sans-serif; font-size: 13px; color: #111827; width: auto; max-width: 100%; box-sizing: border-box;">
        <div style="background-color: #d1d5db; padding: 10px 14px; font-weight: 700; font-size: 15px; color: #111827;">
          ${t("IncomeStatement")}
        </div>
        <div style="border-top: 1px solid #d1d5db; display: flex; justify-content: space-between; padding: 10px 14px;">
          <span style="font-weight: 500;">${t("Expenses")}:</span>
          <span>${expenses} ${t("SAR")}</span>
        </div>
        <div style="border-top: 1px solid #d1d5db; display: flex; justify-content: space-between; padding: 10px 14px;">
          <span style="font-weight: 500;">${t("Revenue")}:</span>
          <span>${revenue} ${t("SAR")}</span>
        </div>
        <div style="border-top: 1px solid #d1d5db; display: flex; justify-content: space-between; padding: 10px 14px;">
          <span style="font-weight: 500;">${t("Income")}:</span>
          <span>${netIncome} ${t("SAR")}</span>
        </div>
      </div>
    `;

    doc.open();
    doc.write(
      `<html><head><title>Income Statement</title><style>@media print {@page {size: A2 portrait; margin: 5mm;} body {margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-family: 'Inter', sans-serif; direction: ${direction};} header, footer {display: none;}}</style></head><body style="font-family:sans-serif; direction:${direction}">`,
    );

    // 3-Column Business Header
    doc.write(`
    <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
      
      <!-- Column 1: Contact Info -->
      <div style="flex: 1; text-align: start; font-size: 13px; color: #333; line-height: 1.6;">
        ${businessAddress ? `<div><span style="font-weight: bold;">${t("Address")}:</span> ${businessAddress}</div>` : ""}
        ${userMobile ? `<div><span style="font-weight: bold;">${t("Phone")}:</span> ${userMobile}</div>` : ""}
        ${userEmail ? `<div><span style="font-weight: bold;">${t("Email")}:</span> ${userEmail}</div>` : ""}
      </div>

      <!-- Column 2: Logo and Main Info (Center) -->
      <div style="flex: 1; text-align: center;">
        ${businessLogo ? `<img src="${businessLogo}" style="height: 75px; margin-bottom: 8px; object-fit: contain;" />` : ""}
        ${businessNameEn ? `<div style="font-weight: bold; font-size: 18px; color: #1f2937;">${businessNameEn}</div>` : ""}
        ${businessNameAr && businessNameAr !== businessNameEn ? `<div style="font-weight: bold; font-size: 18px; color: #1f2937;">${businessNameAr}</div>` : ""}
        <div style="font-weight: bold; font-size: 16px; color: #1f2937; margin-top: 8px;">${t("Income Statement Report")}</div>
        <div style="font-size: 12px; color: #555; margin-top: 5px;">${t("From")}: ${startDate} - ${t("To")}: ${endDate}</div>
      </div>

      <!-- Column 3: Contact Info (Mirrored) -->
      <div style="flex: 1; text-align: end; font-size: 13px; color: #333; line-height: 1.6;">
        ${businessAddress ? `<div><span style="font-weight: bold;">${t("Address")}:</span> ${businessAddress}</div>` : ""}
        ${userMobile ? `<div><span style="font-weight: bold;">${t("Phone")}:</span> ${userMobile}</div>` : ""}
        ${userEmail ? `<div><span style="font-weight: bold;">${t("Email")}:</span> ${userEmail}</div>` : ""}
      </div>
      
    </div>
    `);

    doc.write(tableHTML);
    doc.write("</body></html>");
    doc.close();

    iframe.onload = () => {
      iframe.contentWindow.print();
    };
  };

  const extractData = (element) => {
    const data = [];
    const title = element.querySelector(".text-lg").textContent;
    data.push([title]);
    data.push(["Item", "Amount"]);

    const items = element.querySelectorAll(".flex.justify-between");
    items.forEach((item) => {
      const name = item
        .querySelector(".font-medium")
        .textContent.replace(":", "");
      const amount = item.querySelector("span:last-child").textContent;
      data.push([name, amount]);
    });

    return data;
  };

  const handleExcelExport = () => {
    const reportTitle =
      t("Income Statement Report") || "Income Statement Report";
    const dateRange =
      startDate || endDate
        ? `${t("From")}: ${startDate || ""}  -  ${t("To")}: ${endDate || ""}`
        : "";
    const businessNameEn = user?.Business?.name_en || "";
    const businessNameAr = user?.Business?.name_ar || "";
    const displayBusinessName =
      lang === "ar"
        ? businessNameAr || businessNameEn
        : businessNameEn || businessNameAr;

    const wb = XLSX.utils.book_new();
    const extractedData = extractData(balanceSheetRef.current);

    const headerAoa = [
      [`${displayBusinessName}`],
      [`${reportTitle}`],
      [`${dateRange}`],
      [],
    ];

    const data = [...headerAoa, ...extractedData];
    const ws = XLSX.utils.aoa_to_sheet(data);

    let maxCols = 2;
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: maxCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: maxCols - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: maxCols - 1 } },
    ];

    for (let r = 0; r < 3; r++) {
      const cellRef = XLSX.utils.encode_cell({ c: 0, r });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true, sz: r === 0 ? 16 : 14 },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    }

    data.forEach((row, rowIndex) => {
      if (rowIndex >= 3) {
        if (row.length === 1 && row[0]) {
          const cellRef = XLSX.utils.encode_cell({ c: 0, r: rowIndex });
          if (ws[cellRef]) {
            ws[cellRef].s = {
              font: { bold: true, sz: 14 },
              alignment: { horizontal: "left", vertical: "center" },
            };
          }
          ws["!merges"].push({
            s: { r: rowIndex, c: 0 },
            e: { r: rowIndex, c: maxCols - 1 },
          });
        } else if (row.length === 2 && row[0] === "Item") {
          for (let c = 0; c < 2; c++) {
            const cellRef = XLSX.utils.encode_cell({ c, r: rowIndex });
            if (ws[cellRef])
              ws[cellRef].s = {
                fill: { patternType: "solid", fgColor: { rgb: "D1D5DB" } },
                font: { bold: true },
                alignment: { horizontal: "center", vertical: "center" },
              };
          }
        }
      }
    });

    const colWidths = [{ wch: 40 }, { wch: 20 }];
    ws["!cols"] = colWidths;

    if (lang === "ar") {
      ws["!dir"] = "rtl";
    }

    XLSX.utils.book_append_sheet(wb, ws, "Income Statement");
    XLSX.writeFile(wb, "Income Statement.xlsx");
  };

  return (
    <div>
      <div className="flex justify-end">
        <div className="flex gap-2 flex-col sm:flex-row">
          <DatePicker
            useFormik={false}
            value={startDate ? new Date(startDate) : null}
            onChange={(e) => {
              setStartDate(
                e.value
                  ? new Date(
                      e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
              );
            }}
          />

          <DatePicker
            useFormik={false}
            value={endDate ? new Date(endDate) : null}
            onChange={(e) => {
              setEndDate(
                e.value
                  ? new Date(
                      e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
              );
            }}
          />

          <div className="flex gap-2 items-center">
            <SharedButton
              title={t("apply_filter")}
              icon="pi pi-filter"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!startDate || !endDate}
            />
            <SharedButton
              title={t("Reset")}
              icon="pi pi-sync"
              buttonType="reset"
              onClick={handleReset}
              disabled={isLoading}
            />
            <SharedButton
              title={t("Export Excel")}
              icon={"pi pi-file-excel"}
              iconSize={20}
              onClick={() => handleExcelExport()}
            />
            <SharedButton
              title={t("Export PDF")}
              icon={"pi pi-print"}
              iconSize={20}
              onClick={() => handlePrint()}
            />
          </div>
        </div>
      </div>
      <div className="this_div" ref={balanceSheetRef}>
        {incomeStatement ? (
          <IncomeCard incomeStatement={incomeStatement} />
        ) : null}
      </div>
      <iframe ref={iframeRef} style={{ display: "none" }}></iframe>
    </div>
  );
}
